import { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Text,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

const PAGE_SIZES = [5, 10, 20, 30, 50];

const NavButton = ({ onClick, isDisabled, children }) => (
  <Text
    as="button"
    onClick={onClick}
    disabled={isDisabled}
    color={isDisabled ? '#D0D0D0' : '#4C4C4C'}
    cursor={isDisabled ? 'default' : 'pointer'}
    fontSize="12px"
    lineHeight="1"
    _hover={!isDisabled ? { color: '#000' } : undefined}
    px="4px"
    userSelect="none"
  >
    {children}
  </Text>
);

const ReactTablePagination = ({
  canPreviousPage,
  canNextPage,
  pageOptions,
  pageCount,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
  pageIndex,
  pageSize,
}) => {
  const [goToValue, setGoToValue] = useState(String(pageIndex + 1));
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setGoToValue(String(pageIndex + 1));
    }
  }, [pageIndex]);

  const submitGoTo = () => {
    const page = Number(goToValue);
    if (page >= 1 && page <= pageCount) {
      gotoPage(page - 1);
      setGoToValue(String(page));
    } else {
      setGoToValue(String(pageIndex + 1));
    }
  };

  return (
    <Flex alignItems="center" justifyContent="flex-end" gap="16px">
      <Flex align="center" gap="6px">
        <Text fontSize="11px" color="black" whiteSpace="nowrap">
          Go to page
        </Text>
        <input
          type="number"
          min={1}
          max={pageCount}
          value={goToValue}
          onChange={(e) => setGoToValue(e.target.value)}
          onFocus={() => { isFocusedRef.current = true; }}
          onBlur={() => {
            isFocusedRef.current = false;
            submitGoTo();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          style={{
            width: '40px',
            textAlign: 'center',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid #E2E8F0',
            padding: '2px 4px',
            outline: 'none',
          }}
        />
      </Flex>

      <Menu placement="bottom-end">
        <MenuButton>
          <Flex align="center" gap="4px" cursor="pointer">
            <Text fontSize="11px" color="black" whiteSpace="nowrap">
              Show {pageSize}
            </Text>
            <ChevronDownIcon boxSize="12px" color="black" />
          </Flex>
        </MenuButton>
        <MenuList minW="80px" py={0} borderRadius="6px" boxShadow="md">
          {PAGE_SIZES.map((size) => (
            <MenuItem
              key={size}
              fontSize="12px"
              py="6px"
              px="12px"
              fontWeight={size === pageSize ? 600 : 400}
              onClick={() => setPageSize(size)}
            >
              Show {size}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Flex align="center" gap="6px">
        <NavButton onClick={() => gotoPage(0)} isDisabled={!canPreviousPage}>
          <Icon viewBox="0 0 12 10" w="12px" h="10px">
            <path
              d="M6 1L2 5L6 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11 1L7 5L11 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Icon>
        </NavButton>

        <NavButton onClick={() => previousPage()} isDisabled={!canPreviousPage}>
          <Icon viewBox="0 0 7 10" w="7px" h="10px">
            <path
              d="M6 1L2 5L6 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Icon>
        </NavButton>

        <Text fontSize="11px" color="black" whiteSpace="nowrap" px="4px">
          Page {pageIndex + 1} of {pageOptions.length}
        </Text>

        <NavButton onClick={() => nextPage()} isDisabled={!canNextPage}>
          <Icon viewBox="0 0 7 10" w="7px" h="10px">
            <path
              d="M1 1L5 5L1 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Icon>
        </NavButton>

        <NavButton
          onClick={() => gotoPage(pageCount - 1)}
          isDisabled={!canNextPage}
        >
          <Icon viewBox="0 0 12 10" w="12px" h="10px">
            <path
              d="M6 1L10 5L6 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M1 1L5 5L1 9"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Icon>
        </NavButton>
      </Flex>
    </Flex>
  );
};

export default ReactTablePagination;
